// src/pages/statistiques/StatsDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { COLORS } from '../../constants/colors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
  Filler
);

const formatMontant = (montant) => {
  if (montant === null || montant === undefined || isNaN(montant)) return '0 FCFA';
  return new Intl.NumberFormat('fr-FR').format(montant) + ' FCFA';
};

const formatNombre = (nombre) => {
  if (nombre === null || nombre === undefined || isNaN(nombre)) return '0';
  return new Intl.NumberFormat('fr-FR').format(nombre);
};

export default function StatsDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statsData, setStatsData] = useState({
    membres: {
      total: 0,
      hommes: 0,
      femmes: 0,
      enfants: 0,
      pasteurs: 0,
      anciens: 0,
      diacres: 0,
      nouveaux: 0
    },
    finances: {
      entrees: 0,
      depenses: 0,
      solde: 0,
      mois: []
    },
    evenements: {
      assistances: 0,
      mariages: 0,
      baptemes: 0,
      naissances: 0,
      deces: 0,
      totalParticipants: 0
    },
    eglises: {
      total: 0,
      sieges: 0,
      temples: 0
    }
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        navigate('/pages/signin2');
        return;
      }

      const user = JSON.parse(userStr);
      const userId = user.id || user.id_utilisateur;
      
      const response = await fetch(`https://josephsbusiness.com/api/stats/dashboard.php?user_id=${userId}`, {
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setStatsData(result.data);
      } else {
        setError(result.message || 'Erreur de chargement des statistiques');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <div className="text-center">
              <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
              <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des statistiques</h5>
              <p className="text-muted">Récupération des données en cours...</p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  // Vérifier si les données financières existent
  const hasFinances = statsData.finances.mois && statsData.finances.mois.length > 0;
  
  // Déterminer l'année actuelle et l'année précédente pour les libellés
  const today = new Date();
  const currentYear = today.getFullYear();
  const previousYear = currentYear - 1;
  
  // Données pour les graphiques
  const repartitionMembresData = {
    labels: ['Hommes', 'Femmes', 'Enfants'],
    datasets: [
      {
        data: [
          statsData.membres.hommes || 0, 
          statsData.membres.femmes || 0, 
          statsData.membres.enfants || 0
        ],
        backgroundColor: [COLORS.info, COLORS.pink, COLORS.warning],
        borderColor: 'white',
        borderWidth: 2,
      },
    ],
  };

  const rolesMembresData = {
    labels: ['Pasteurs', 'Anciens', 'Diacres', 'Nouveaux'],
    datasets: [
      {
        data: [
          statsData.membres.pasteurs || 0,
          statsData.membres.anciens || 0,
          statsData.membres.diacres || 0,
          statsData.membres.nouveaux || 0
        ],
        backgroundColor: [COLORS.purple, COLORS.info, COLORS.success, COLORS.warning],
        borderColor: 'white',
        borderWidth: 2,
      },
    ],
  };

  // Préparer les données financières avec l'année
  const moisFinances = hasFinances ? [...statsData.finances.mois].reverse() : [];
  
  // Créer des libellés avec l'année
  const moisAvecAnnee = moisFinances.map((item, index) => {
    const moisIndex = moisFinances.length - 1 - index;
    if (index >= 9) {
      return `${item.mois} ${currentYear}`;
    } else {
      return `${item.mois} ${previousYear}`;
    }
  });
  
  const financesData = {
    labels: moisAvecAnnee,
    datasets: [
      {
        label: 'Entrées',
        data: moisFinances.map(m => m.entrees || 0),
        backgroundColor: COLORS.success + '80',
        borderColor: COLORS.success,
        borderWidth: 2,
      },
      {
        label: 'Dépenses',
        data: moisFinances.map(m => m.depenses || 0),
        backgroundColor: COLORS.danger + '80',
        borderColor: COLORS.danger,
        borderWidth: 2,
      },
    ],
  };

  const evenementsData = {
    labels: ['Assistances', 'Mariages', 'Baptêmes', 'Naissances', 'Décès'],
    datasets: [
      {
        data: [
          statsData.evenements.assistances || 0,
          statsData.evenements.mariages || 0,
          statsData.evenements.baptemes || 0,
          statsData.evenements.naissances || 0,
          statsData.evenements.deces || 0
        ],
        backgroundColor: [
          COLORS.purple,
          COLORS.pink,
          COLORS.info,
          COLORS.success,
          COLORS.gray
        ],
        borderColor: 'white',
        borderWidth: 2,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 11 },
          padding: 15
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.raw;
            if (value === null || value === undefined || isNaN(value)) return `${label}: 0`;
            if (context.dataset.label?.includes('Entrées') || context.dataset.label?.includes('Dépenses')) {
              return `${label}: ${formatMontant(value)}`;
            } else {
              return `${label}: ${formatNombre(value)}`;
            }
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function(value) {
            if (value >= 1000000) return (value / 1000000).toFixed(1) + 'M';
            else if (value >= 1000) return (value / 1000).toFixed(0) + 'k';
            return value;
          }
        }
      }
    }
  };

  const totalEvenements = 
    (statsData.evenements.assistances || 0) + 
    (statsData.evenements.mariages || 0) + 
    (statsData.evenements.baptemes || 0) + 
    (statsData.evenements.naissances || 0) + 
    (statsData.evenements.deces || 0);

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)' }}>
        <div className="container-fluid px-0 px-lg-3">

          {/* ===== EN‑TÊTE MODERNE ===== */}
          <div className="header-modern mb-4 fade-in">
            <div className="header-left">
              <div className="header-icon-wrapper" style={{ background: COLORS.primary }}>
                <i className="ri-bar-chart-2-line"></i>
              </div>
              <div>
                <h5 className="gradient-text">Tableau de bord statistiques</h5>
                <div className="d-flex flex-wrap gap-2">
                  <span className="badge-modern">
                    <i className="ri-group-line"></i> Membres {formatNombre(statsData.membres.total)}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                    <i className="ri-money-euro-circle-line"></i> Solde {formatMontant(statsData.finances.solde)}
                  </span>
                  <span className="badge-modern" style={{ background: `${COLORS.purple}15`, color: COLORS.purple }}>
                    <i className="ri-calendar-line"></i> Événements {formatNombre(totalEvenements)}
                  </span>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2">
              <button className="btn-modern-outline" onClick={fetchStats} disabled={loading}>
                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i> Actualiser
              </button>
            </div>
          </div>

          {error && (
            <Alert variant="danger" className="mb-3" style={{ borderRadius: '14px', borderLeft: `4px solid ${COLORS.danger}` }}>
              <div className="d-flex align-items-center">
                <i className="ri-error-warning-line me-2" style={{ color: COLORS.danger, fontSize: '1.2rem' }}></i>
                <span>{error}</span>
              </div>
            </Alert>
          )}

          {/* ===== KPI CARDS ===== */}
          <Row className="g-3 mb-4">
            <Col xs="6" xl="3">
              <div className="kpi-card kpi-primary">
                <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                <div className="kpi-content">
                  <span className="kpi-label">Membres</span>
                  <h3 className="kpi-value">{formatNombre(statsData.membres.total)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: COLORS.success }}>
                <div className="kpi-icon" style={{ background: 'rgba(16,185,129,0.1)', color: COLORS.success }}>
                  <i className="ri-money-euro-circle-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Solde</span>
                  <h3 className="kpi-value">{formatMontant(statsData.finances.solde)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: COLORS.purple }}>
                <div className="kpi-icon" style={{ background: 'rgba(139,92,246,0.1)', color: COLORS.purple }}>
                  <i className="ri-calendar-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Événements</span>
                  <h3 className="kpi-value">{formatNombre(totalEvenements)}</h3>
                </div>
              </div>
            </Col>
            <Col xs="6" xl="3">
              <div className="kpi-card" style={{ borderLeftColor: COLORS.info }}>
                <div className="kpi-icon" style={{ background: 'rgba(59,130,246,0.1)', color: COLORS.info }}>
                  <i className="ri-building-line fs-24"></i>
                </div>
                <div className="kpi-content">
                  <span className="kpi-label">Églises</span>
                  <h3 className="kpi-value">{formatNombre(statsData.eglises.total)}</h3>
                </div>
              </div>
            </Col>
          </Row>

          {/* ===== GRAPHIQUES ===== */}
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="chart-card-modern" style={{ border: 'none', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Card.Header style={{ background: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(0,0,0,0.04)', padding: '0.75rem 1.25rem' }}>
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-pie-chart-line me-2"></i>
                    Répartition des membres
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {statsData.membres.total > 0 ? (
                    <Pie data={repartitionMembresData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Aucune donnée disponible</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="chart-card-modern" style={{ border: 'none', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Card.Header style={{ background: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(0,0,0,0.04)', padding: '0.75rem 1.25rem' }}>
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-bar-chart-grouped-line me-2"></i>
                    Évolution financière (6 derniers mois)
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {hasFinances && moisFinances.some(m => m.entrees > 0 || m.depenses > 0) ? (
                    <Bar data={financesData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Aucune donnée financière disponible</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={6}>
              <Card className="chart-card-modern" style={{ border: 'none', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Card.Header style={{ background: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(0,0,0,0.04)', padding: '0.75rem 1.25rem' }}>
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-user-star-line me-2"></i>
                    Rôles des membres
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {statsData.membres.pasteurs + statsData.membres.anciens + statsData.membres.diacres + statsData.membres.nouveaux > 0 ? (
                    <Pie data={rolesMembresData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Aucune donnée disponible</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="chart-card-modern" style={{ border: 'none', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Card.Header style={{ background: 'rgba(255,255,255,0.5)', borderBottom: '1px solid rgba(0,0,0,0.04)', padding: '0.75rem 1.25rem' }}>
                  <h6 className="fw-semibold mb-0" style={{ color: COLORS.primary, fontSize: '0.95rem' }}>
                    <i className="ri-calendar-event-line me-2"></i>
                    Répartition des événements
                  </h6>
                </Card.Header>
                <Card.Body style={{ height: '280px', padding: '1rem' }}>
                  {totalEvenements > 0 ? (
                    <Pie data={evenementsData} options={chartOptions} />
                  ) : (
                    <div className="d-flex align-items-center justify-content-center h-100 text-muted small">Aucune donnée disponible</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ===== RÉSUMÉ FINANCIER ===== */}
          {hasFinances && (
            <Row className="mt-4">
              <Col md={12}>
                <Card className="stat-card-modern" style={{ border: 'none', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
                  <Card.Body className="p-3">
                    <div className="d-flex flex-wrap gap-4">
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total entrées (année)</small>
                        <span className="fw-bold" style={{ color: COLORS.success, fontSize: '1.2rem' }}>{formatMontant(statsData.finances.entrees)}</span>
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total dépenses (année)</small>
                        <span className="fw-bold" style={{ color: COLORS.danger, fontSize: '1.2rem' }}>{formatMontant(statsData.finances.depenses)}</span>
                      </div>
                      <div>
                        <small className="text-muted d-block" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Solde</small>
                        <span className="fw-bold" style={{ color: statsData.finances.solde >= 0 ? COLORS.success : COLORS.danger, fontSize: '1.2rem' }}>
                          {formatMontant(statsData.finances.solde)}
                        </span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          <Footer />
        </div>
      </div>

      {/* ===== STYLES ===== */}
      <style>{`
        .fade-in {
          animation: fadeIn 0.3s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .header-modern {
          background: white;
          padding: 1.2rem 1.8rem;
          box-shadow: 0 2px 8px rgba(0,0,0,0.04);
          border-left: 4px solid ${COLORS.secondary};
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
        }
        .header-left {
          display: flex;
          align-items: center;
          gap: 1rem;
        }
        .header-icon-wrapper {
          width: 48px;
          height: 48px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.4rem;
        }
        .gradient-text {
          color: #1e293b;
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0;
        }
        .badge-modern {
          background: #f1f4f9;
          color: #475569;
          padding: 0.3rem 0.8rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .badge-modern i {
          color: ${COLORS.secondary};
        }
        .btn-modern-outline {
          border: 1px solid #d1d5db;
          background: white;
          padding: 0.4rem 1.2rem;
          border-radius: 4px;
          font-size: 0.85rem;
          font-weight: 500;
          color: #4b5563;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .btn-modern-outline:hover {
          border-color: ${COLORS.secondary};
          color: ${COLORS.secondary};
          background: #fafafa;
        }

        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: ${COLORS.primary}; }
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
        .kpi-content { flex: 1; }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .fs-24 { font-size: 1.5rem; }

        .chart-card-modern {
          transition: all 0.3s ease;
        }
        .chart-card-modern:hover {
          box-shadow: 0 16px 40px rgba(0,0,0,0.08) !important;
        }

        .stat-card-modern {
          transition: all 0.3s ease;
        }
        .stat-card-modern:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 32px rgba(0,0,0,0.08) !important;
        }

        @media (max-width: 768px) {
          .header-modern {
            flex-direction: column;
            align-items: stretch;
            gap: 0.8rem;
            padding: 1rem;
          }
          .header-left {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </>
  );
}